'use client';

import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { moonpayApi, type MoonpayKycState } from '@/services/api/moonpay.api';

import { MOONPAY_COUNTRIES } from '@/constants';

/**
 * Dynamic KYC capture — HEADLESS (Customer-API) countries only. Renders our own
 * screens from MoonPay's live `kyc.requirements` (MoonPay verifies; we own the
 * UX). The residence country is fixed by the selector (headless-supported), so
 * there is no country picker and no hosted hand-off here.
 * Fields/docs/questionnaires shown depend on the country, per
 * dev.moonpay.com/platform/guides/kyc-data-requirements.
 */

interface MoonpayKycStepProps {
  state: MoonpayKycState;
  onSubmitted: (state: MoonpayKycState) => void;
  /** ISO 3166-1 alpha-3 residence country, locked from the selector (e.g. 'MEX'). */
  country: string;
}

const inputCls =
  'w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none transition-colors focus:border-[var(--primary)] placeholder:text-[var(--text-muted)]';
const inputErrCls =
  'w-full rounded-xl border border-[var(--accent-red)] bg-[var(--bg-primary)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none transition-colors focus:border-[var(--accent-red)] placeholder:text-[var(--text-muted)]';
const labelCls = 'mb-1.5 block text-xs font-semibold text-[var(--text-muted)]';

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <label className={labelCls}>{label}</label>
      {children}
      {error && (
        <span className="mt-1 block text-[11px] font-medium text-[var(--accent-red)]">{error}</span>
      )}
    </Box>
  );
}

const DOC_TYPES = [
  { value: 'passport', label: 'Passport', back: false },
  { value: 'drivingLicence', label: 'Driving licence', back: true },
  { value: 'nationalIdentityCard', label: 'National ID card', back: true },
  { value: 'residencePermit', label: 'Residence permit', back: true },
];

// Tax identifier TYPE per MoonPay's Customer API. Only `ssn` (US), `cpf` (Brazil)
// and `tin` (everywhere else) are valid types — `rfc`/`cuit` etc. are NOT and get
// rejected with a 400. For our LatAm headless set that means Brazil→cpf, rest→tin.
const taxIdInfo = (a3: string) =>
  String(a3 || '').toUpperCase() === 'BRA'
    ? { type: 'cpf', label: 'CPF', needsCountry: false }
    : { type: 'tin', label: 'Tax identifier (TIN)', needsCountry: true };

// State/province NOT required by MoonPay for these markets; required elsewhere.
const ADMIN_AREA_OPTIONAL = new Set(['ARG', 'SLV', 'HND', 'PAN']);

// ── Due-diligence questionnaire option sets (dev.moonpay.com/.../kyc-data-requirements) ──
const EMPLOYMENT = ['employedOrSelfEmployed', 'retired', 'student', 'unemployed'];
const ACCOUNT_PURPOSE = [
  'investing',
  'payments',
  'purchasingDigitalAssets',
  'purchasingGoodsOrServices',
  'trading',
];
const SOURCE_OF_WEALTH = ['salary', 'savings', 'investments', 'cryptoTrading', 'other'];
const PROFESSION = [
  'agriculture',
  'artsAndEntertainment',
  'businessOwner',
  'education',
  'financialServices',
  'healthcare',
  'industrialTradesAndTransport',
  'informationAndTechnology',
  'legalAndProfessionalServices',
  'publicSector',
  'realEstate',
  'retail',
  'seniorManagement',
];
// Reporting currency for every LatAm headless market is USD.
const REPORTING_CURRENCY = 'USD';
const labelize = (v: string) =>
  v
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

export const MoonpayKycStep = ({ state, onSubmitted, country }: MoonpayKycStepProps) => {
  const { t } = useT();
  const req = state.requirements || {};
  const a3 = String(country || '').toUpperCase();

  const isNeeded = (cat: string) => {
    const r = (req as any)[cat];
    return r && (r.status ? r.status !== 'complete' : true);
  };
  const requiredFields = (cat: string): string[] => {
    const r = (req as any)[cat];
    return Array.isArray(r?.requiredFields) ? r.requiredFields : [];
  };

  // Profile fields (country is fixed by the selector).
  const [form, setForm] = useState<Record<string, string>>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    nationality: a3,
    street: '',
    subStreet: '',
    locality: '',
    administrativeArea: '',
    postalCode: '',
    phoneNumber: '',
    taxValue: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setFieldErrors((er) => {
      if (!er[k]) return er;
      const next = { ...er };
      delete next[k];
      return next;
    });
  };
  const inCls = (k: string) => (fieldErrors[k] ? inputErrCls : inputCls);

  // Questionnaire answers (only rendered/submitted when required).
  const [q, setQ] = useState<Record<string, string>>({
    employmentStatus: '',
    accountPurpose: '',
    grossAnnualIncome: '',
    sourceOfWealth: '',
    transactionFrequencyPerMonth: '',
    expectedTransactionAmountPerMonth: '',
    profession: '',
    netWorth: '',
  });
  const setQv = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setQ((s) => ({ ...s, [k]: e.target.value }));
    setFieldErrors((er) => {
      if (!er[k]) return er;
      const next = { ...er };
      delete next[k];
      return next;
    });
  };

  // Documents
  const [docType, setDocType] = useState('passport');
  const [docFront, setDocFront] = useState<File | null>(null);
  const [docBack, setDocBack] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [poa, setPoa] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Until KYC is kicked off, MoonPay returns empty requirements. Show the base
  // profile so submitting it (esp. residentialAddress.country) populates the
  // full requirement set (documents/selfie/tax/questionnaires).
  const reqEmpty = Object.keys(req).length === 0;
  const needsBasic = reqEmpty || isNeeded('basicDetails');
  const needsAddress = reqEmpty || isNeeded('residentialAddress');
  const needsPhone = reqEmpty || isNeeded('phoneNumber');
  const needsTax = isNeeded('taxIdentifiers');
  const needsDoc = isNeeded('identityDocuments');
  const needsSelfie = isNeeded('selfie');
  const needsPoa = isNeeded('proofOfAddress');
  const qFields = requiredFields('questionnaires');
  const needsCdd = isNeeded('questionnaires') && qFields.includes('customerDueDiligence');
  const needsEdd = isNeeded('questionnaires') && qFields.includes('enhancedDueDiligence');

  const backRequired = useMemo(() => DOC_TYPES.find((d) => d.value === docType)?.back, [docType]);
  const { type: taxIdType, label: taxIdLabel, needsCountry: taxNeedsCountry } = taxIdInfo(a3);
  const adminAreaRequired = !ADMIN_AREA_OPTIONAL.has(a3);

  const uploadDoc = async (file: File, fileType: string, side?: string) => {
    const { uploadId, uploadUrl } = await moonpayApi.getUploadUrl({
      fileType,
      mimeType: file.type,
    });
    await moonpayApi.uploadToPresignedUrl(uploadUrl, file);
    await moonpayApi.confirmFile({ uploadId, fileType, side });
  };

  // Client-side validation mirroring MoonPay's constraints (also enforced server-side).
  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    const required = t('moonpay.kyc.required', { defaultValue: 'Required' });
    const positive = t('moonpay.kyc.positive', { defaultValue: 'Enter an amount greater than 0' });
    if (needsBasic) {
      if (!form.firstName.trim()) e.firstName = required;
      if (!form.lastName.trim()) e.lastName = required;
      if (!form.dateOfBirth) e.dateOfBirth = required;
      else {
        const dob = new Date(form.dateOfBirth);
        const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000);
        if (Number.isNaN(dob.getTime())) e.dateOfBirth = required;
        else if (age < 18)
          e.dateOfBirth = t('moonpay.kyc.min18', { defaultValue: 'You must be 18 or older' });
      }
      if (!form.nationality.trim()) e.nationality = required;
    }
    if (needsAddress) {
      if (!form.street.trim()) e.street = required;
      const loc = form.locality.trim();
      if (!loc) e.locality = required;
      else if (loc.length < 3)
        e.locality = t('moonpay.kyc.min3', { defaultValue: 'At least 3 characters' });
      else if (!/^[^0-9<>&"/]+$/.test(loc))
        e.locality = t('moonpay.kyc.noDigits', { defaultValue: 'No numbers or symbols' });
      const pc = form.postalCode.trim();
      if (!pc) e.postalCode = required;
      else if (pc.length < 3)
        e.postalCode = t('moonpay.kyc.min3', { defaultValue: 'At least 3 characters' });
      else if (!/^[^<>&"]+$/.test(pc))
        e.postalCode = t('moonpay.kyc.invalidChars', { defaultValue: 'Invalid characters' });
      if (adminAreaRequired && !form.administrativeArea.trim()) e.administrativeArea = required;
    }
    if (needsPhone) {
      const ph = form.phoneNumber.trim();
      if (!ph) e.phoneNumber = required;
      else if (!/^\+[1-9]\d{6,14}$/.test(ph))
        e.phoneNumber = t('moonpay.kyc.invalidPhone', {
          defaultValue: 'Use international format, e.g. +52…',
        });
    }
    if (needsTax && !form.taxValue.trim()) e.taxValue = required;
    if (needsCdd) {
      if (!q.employmentStatus) e.employmentStatus = required;
      if (!q.accountPurpose) e.accountPurpose = required;
      if (!q.sourceOfWealth) e.sourceOfWealth = required;
      if (!(Number(q.grossAnnualIncome) > 0)) e.grossAnnualIncome = positive;
      if (!(Number(q.expectedTransactionAmountPerMonth) > 0))
        e.expectedTransactionAmountPerMonth = positive;
      if (
        !(
          Number.isInteger(Number(q.transactionFrequencyPerMonth)) &&
          Number(q.transactionFrequencyPerMonth) > 0
        )
      )
        e.transactionFrequencyPerMonth = t('moonpay.kyc.positiveInt', {
          defaultValue: 'Enter a whole number greater than 0',
        });
    }
    if (needsEdd) {
      if (!q.profession) e.profession = required;
      if (!(Number(q.netWorth) > 0)) e.netWorth = positive;
    }
    return e;
  };

  const handleSubmit = async () => {
    setError(null);
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError(t('moonpay.kyc.fixFields', { defaultValue: 'Please fix the highlighted fields.' }));
      return;
    }
    setSubmitting(true);
    try {
      // 1. Field-based categories
      const payload: Record<string, any> = {};
      if (needsBasic) {
        payload.basicDetails = {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          dateOfBirth: form.dateOfBirth,
          nationality: form.nationality.trim().toUpperCase(),
        };
      }
      if (needsAddress) {
        payload.residentialAddress = {
          street: form.street.trim(),
          ...(form.subStreet ? { subStreet: form.subStreet.trim() } : {}),
          locality: form.locality.trim(),
          ...(form.administrativeArea
            ? { administrativeArea: form.administrativeArea.trim() }
            : {}),
          postalCode: form.postalCode.trim(),
          country: a3, // locked residence country (alpha-3)
        };
      }
      if (needsPhone) {
        payload.phoneNumber = { number: form.phoneNumber.trim() };
      }
      if (needsTax && form.taxValue) {
        payload.taxIdentifiers = [
          {
            type: taxIdType,
            value: form.taxValue.trim(),
            ...(taxNeedsCountry ? { country: a3 } : {}),
          },
        ];
      }
      // Due-diligence questionnaires (monetary amounts in the reporting currency, USD).
      const questionnaires: any[] = [];
      if (needsCdd) {
        questionnaires.push({
          type: 'customerDueDiligence',
          answers: {
            employmentStatus: q.employmentStatus,
            accountPurpose: q.accountPurpose,
            grossAnnualIncome: {
              currency: REPORTING_CURRENCY,
              amount: String(q.grossAnnualIncome).trim(),
            },
            sourceOfWealth: q.sourceOfWealth,
            transactionFrequencyPerMonth: Number(q.transactionFrequencyPerMonth),
            expectedTransactionAmountPerMonth: {
              currency: REPORTING_CURRENCY,
              amount: String(q.expectedTransactionAmountPerMonth).trim(),
            },
          },
        });
      }
      if (needsEdd) {
        questionnaires.push({
          type: 'enhancedDueDiligence',
          answers: {
            profession: q.profession,
            netWorth: { currency: REPORTING_CURRENCY, amount: String(q.netWorth).trim() },
          },
        });
      }
      if (questionnaires.length) payload.questionnaires = questionnaires;

      if (Object.keys(payload).length > 0) {
        await moonpayApi.submitKyc(payload);
      }

      // 2. Document uploads
      if (needsDoc && docFront) {
        await uploadDoc(docFront, 'identityDocuments', backRequired ? 'front' : undefined);
        if (backRequired && docBack) await uploadDoc(docBack, 'identityDocuments', 'back');
      }
      if (needsSelfie && selfie) await uploadDoc(selfie, 'selfie');
      if (needsPoa && poa) await uploadDoc(poa, 'proofOfAddress');

      // Re-read the customer: MoonPay may now list NEW requirements (documents/
      // selfie/questionnaires) or move to verifying. The parent routes onward.
      const finalState = await moonpayApi.getCustomer();
      onSubmitted(finalState);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const FileInput = ({
    file,
    onPick,
    label,
  }: {
    file: File | null;
    onPick: (f: File | null) => void;
    label: string;
  }) => (
    <Field label={label}>
      <label className="flex cursor-pointer items-center justify-between rounded-xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2.5 text-sm text-[var(--text-muted)] hover:border-[var(--primary)]">
        <span className="truncate">
          {file ? file.name : t('moonpay.kyc.chooseFile', { defaultValue: 'Choose file…' })}
        </span>
        <input
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] || null)}
        />
      </label>
    </Field>
  );

  const Select = ({
    k,
    value,
    onChange,
    options,
  }: {
    k: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: string[];
  }) => (
    <select className={inCls(k)} value={value} onChange={onChange}>
      <option value="">{t('moonpay.kyc.select', { defaultValue: 'Select…' })}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {labelize(o)}
        </option>
      ))}
    </select>
  );

  return (
    <Box className="flex flex-col gap-5">
      <Box>
        <Typography className="text-lg font-bold text-[var(--text-primary)]">
          {t('moonpay.kyc.title', { defaultValue: 'Verify your identity' })}
        </Typography>
        <Typography className="mt-1 text-sm text-[var(--text-muted)]">
          {t('moonpay.kyc.subtitle', {
            defaultValue: 'A one-time check required by our payment partner to move funds.',
          })}
        </Typography>
      </Box>

      {needsBasic && (
        <Box className="flex flex-col gap-3">
          <Typography className="text-sm font-bold text-[var(--text-primary)]">
            {t('moonpay.kyc.personal', { defaultValue: 'Personal details' })}
          </Typography>
          <Box className="grid grid-cols-2 gap-3">
            <Field
              label={t('moonpay.kyc.firstName', { defaultValue: 'First name' })}
              error={fieldErrors.firstName}
            >
              <input
                className={inCls('firstName')}
                value={form.firstName}
                onChange={set('firstName')}
              />
            </Field>
            <Field
              label={t('moonpay.kyc.lastName', { defaultValue: 'Last name' })}
              error={fieldErrors.lastName}
            >
              <input
                className={inCls('lastName')}
                value={form.lastName}
                onChange={set('lastName')}
              />
            </Field>
            <Field
              label={t('moonpay.kyc.dob', { defaultValue: 'Date of birth' })}
              error={fieldErrors.dateOfBirth}
            >
              <input
                type="date"
                className={inCls('dateOfBirth')}
                value={form.dateOfBirth}
                onChange={set('dateOfBirth')}
              />
            </Field>
            <Field
              label={t('moonpay.kyc.nationality', { defaultValue: 'Nationality' })}
              error={fieldErrors.nationality}
            >
              <select
                className={inCls('nationality')}
                value={form.nationality}
                onChange={set('nationality')}
              >
                {MOONPAY_COUNTRIES.map((c) => (
                  <option key={c.a3} value={c.a3}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </Box>
        </Box>
      )}

      {needsAddress && (
        <Box className="flex flex-col gap-3">
          <Typography className="text-sm font-bold text-[var(--text-primary)]">
            {t('moonpay.kyc.address', { defaultValue: 'Residential address' })}
          </Typography>
          <Field
            label={t('moonpay.kyc.street', { defaultValue: 'Street address' })}
            error={fieldErrors.street}
          >
            <input className={inCls('street')} value={form.street} onChange={set('street')} />
          </Field>
          <Box className="grid grid-cols-2 gap-3">
            <Field
              label={t('moonpay.kyc.city', { defaultValue: 'City' })}
              error={fieldErrors.locality}
            >
              <input
                className={inCls('locality')}
                value={form.locality}
                onChange={set('locality')}
              />
            </Field>
            <Field
              label={
                adminAreaRequired
                  ? t('moonpay.kyc.state', { defaultValue: 'State / province' })
                  : t('moonpay.kyc.stateOptional', { defaultValue: 'State / province (optional)' })
              }
              error={fieldErrors.administrativeArea}
            >
              <input
                className={inCls('administrativeArea')}
                value={form.administrativeArea}
                onChange={set('administrativeArea')}
              />
            </Field>
            <Field
              label={t('moonpay.kyc.postal', { defaultValue: 'Postal code' })}
              error={fieldErrors.postalCode}
            >
              <input
                className={inCls('postalCode')}
                value={form.postalCode}
                onChange={set('postalCode')}
              />
            </Field>
            <Field label={t('moonpay.kyc.country', { defaultValue: 'Country of residence' })}>
              <input className={`${inputCls} opacity-70`} value={a3} readOnly disabled />
            </Field>
          </Box>
        </Box>
      )}

      {needsPhone && (
        <Field
          label={t('moonpay.kyc.phone', { defaultValue: 'Phone number (E.164, e.g. +52…)' })}
          error={fieldErrors.phoneNumber}
        >
          <input
            className={inCls('phoneNumber')}
            placeholder="+52..."
            value={form.phoneNumber}
            onChange={set('phoneNumber')}
          />
        </Field>
      )}

      {needsTax && (
        <Field label={taxIdLabel} error={fieldErrors.taxValue}>
          <input
            className={inCls('taxValue')}
            placeholder={taxIdLabel}
            value={form.taxValue}
            onChange={set('taxValue')}
          />
        </Field>
      )}

      {(needsCdd || needsEdd) && (
        <Box className="flex flex-col gap-3">
          <Typography className="text-sm font-bold text-[var(--text-primary)]">
            {t('moonpay.kyc.dueDiligence', { defaultValue: 'A few more questions' })}
          </Typography>
          <Typography className="-mt-2 text-xs text-[var(--text-muted)]">
            {t('moonpay.kyc.amountsUsd', { defaultValue: 'Amounts in USD.' })}
          </Typography>

          {needsCdd && (
            <>
              <Field
                label={t('moonpay.kyc.employment', { defaultValue: 'Employment status' })}
                error={fieldErrors.employmentStatus}
              >
                <Select
                  k="employmentStatus"
                  value={q.employmentStatus}
                  onChange={setQv('employmentStatus')}
                  options={EMPLOYMENT}
                />
              </Field>
              <Field
                label={t('moonpay.kyc.accountPurpose', { defaultValue: 'Account purpose' })}
                error={fieldErrors.accountPurpose}
              >
                <Select
                  k="accountPurpose"
                  value={q.accountPurpose}
                  onChange={setQv('accountPurpose')}
                  options={ACCOUNT_PURPOSE}
                />
              </Field>
              <Field
                label={t('moonpay.kyc.sourceOfWealth', { defaultValue: 'Source of wealth' })}
                error={fieldErrors.sourceOfWealth}
              >
                <Select
                  k="sourceOfWealth"
                  value={q.sourceOfWealth}
                  onChange={setQv('sourceOfWealth')}
                  options={SOURCE_OF_WEALTH}
                />
              </Field>
              <Box className="grid grid-cols-2 gap-3">
                <Field
                  label={t('moonpay.kyc.grossIncome', {
                    defaultValue: 'Gross annual income (USD)',
                  })}
                  error={fieldErrors.grossAnnualIncome}
                >
                  <input
                    className={inCls('grossAnnualIncome')}
                    inputMode="decimal"
                    placeholder="0"
                    value={q.grossAnnualIncome}
                    onChange={setQv('grossAnnualIncome')}
                  />
                </Field>
                <Field
                  label={t('moonpay.kyc.txPerMonth', { defaultValue: 'Transactions / month' })}
                  error={fieldErrors.transactionFrequencyPerMonth}
                >
                  <input
                    className={inCls('transactionFrequencyPerMonth')}
                    inputMode="numeric"
                    placeholder="0"
                    value={q.transactionFrequencyPerMonth}
                    onChange={setQv('transactionFrequencyPerMonth')}
                  />
                </Field>
              </Box>
              <Field
                label={t('moonpay.kyc.expectedMonthly', {
                  defaultValue: 'Expected amount / month (USD)',
                })}
                error={fieldErrors.expectedTransactionAmountPerMonth}
              >
                <input
                  className={inCls('expectedTransactionAmountPerMonth')}
                  inputMode="decimal"
                  placeholder="0"
                  value={q.expectedTransactionAmountPerMonth}
                  onChange={setQv('expectedTransactionAmountPerMonth')}
                />
              </Field>
            </>
          )}

          {needsEdd && (
            <>
              <Field
                label={t('moonpay.kyc.profession', { defaultValue: 'Profession' })}
                error={fieldErrors.profession}
              >
                <Select
                  k="profession"
                  value={q.profession}
                  onChange={setQv('profession')}
                  options={PROFESSION}
                />
              </Field>
              <Field
                label={t('moonpay.kyc.netWorth', { defaultValue: 'Net worth (USD)' })}
                error={fieldErrors.netWorth}
              >
                <input
                  className={inCls('netWorth')}
                  inputMode="decimal"
                  placeholder="0"
                  value={q.netWorth}
                  onChange={setQv('netWorth')}
                />
              </Field>
            </>
          )}
        </Box>
      )}

      {needsDoc && (
        <Box className="flex flex-col gap-3">
          <Typography className="text-sm font-bold text-[var(--text-primary)]">
            {t('moonpay.kyc.idDoc', { defaultValue: 'Identity document' })}
          </Typography>
          <Field label={t('moonpay.kyc.docType', { defaultValue: 'Document type' })}>
            <select
              className={inputCls}
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            >
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </Field>
          <FileInput
            file={docFront}
            onPick={setDocFront}
            label={t('moonpay.kyc.front', { defaultValue: 'Front' })}
          />
          {backRequired && (
            <FileInput
              file={docBack}
              onPick={setDocBack}
              label={t('moonpay.kyc.back', { defaultValue: 'Back' })}
            />
          )}
        </Box>
      )}

      {needsSelfie && (
        <FileInput
          file={selfie}
          onPick={setSelfie}
          label={t('moonpay.kyc.selfie', { defaultValue: 'Selfie' })}
        />
      )}
      {needsPoa && (
        <FileInput
          file={poa}
          onPick={setPoa}
          label={t('moonpay.kyc.poa', { defaultValue: 'Proof of address (last 90 days)' })}
        />
      )}

      {error && (
        <Box className="flex items-start gap-2">
          <Box className="text-[var(--accent-red)]">⚠️</Box>
          <Typography className="text-xs text-[var(--accent-red)]">{error}</Typography>
        </Box>
      )}

      <button
        type="button"
        disabled={submitting}
        onClick={handleSubmit}
        className="mt-1 w-full rounded-2xl bg-[var(--primary)] px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {submitting
          ? t('moonpay.kyc.submitting', { defaultValue: 'Submitting…' })
          : t('moonpay.kyc.submit', { defaultValue: 'Submit for verification' })}
      </button>
    </Box>
  );
};
